import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  useGridApiContext
} from '@mui/x-data-grid-premium';

import Icon from '../../../../../../../../../common/general/Icon';
import { compareDates, DEFAULT_DATE_FORMAT, formatDate } from '../../../../../../../../../services/date';

import { MAX_COMMENT_LENGTH } from './taskCommentConstants';
import { TaskCommentRow } from './taskCommentTypes';

const TaskIcon = Icon as React.ComponentType<{ children: React.ReactNode }>;

type CreateTaskCommentsColumnsParams = {
  currentCommentText: string;
  rowModesModel: GridRowModesModel;
  isSaving: boolean;
  onCommentChange: (value: string) => void;
  onAddRow: () => void;
  onMarkSaveIntent: (id: GridRowId) => void;
  onSaveClick: (id: GridRowId) => () => void;
  onCancelClick: (id: GridRowId) => () => void;
};

export default function createTaskCommentsColumns({
  currentCommentText,
  rowModesModel,
  isSaving,
  onCommentChange,
  onAddRow,
  onMarkSaveIntent,
  onSaveClick,
  onCancelClick
}: CreateTaskCommentsColumnsParams): GridColDef<TaskCommentRow>[] {
  return [
    {
      field: 'creationDate',
      headerName: 'Date Created',
      width: 110,
      sortComparator: compareDates,
      renderCell: ({ value }) => formatDate(value as string | null, DEFAULT_DATE_FORMAT, '-')
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      minWidth: 150,
      valueGetter: (_, row) => `${row.createdByFirstName || ''} ${row.createdByLastName || ''}`.trim() || '-'
    },
    {
      field: 'commentText',
      headerName: 'Comment',
      flex: 2,
      minWidth: 460,
      editable: true,
      cellClassName: ({ row }) => (row.isNew ? 'task-comments-modal-comment-grid-cell' : ''),
      renderCell: ({ value }) => {
        const commentText = String(value || '');
        return (
          <div className="task-comments-modal-comment-cell" title={commentText}>
            {commentText || '-'}
          </div>
        );
      },
      renderEditCell: params => <CommentEditCell {...params} onCommentChange={onCommentChange} />
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 82,
      minWidth: 82,
      cellClassName: 'task-comments-modal-actions-cell',
      renderHeader: () => <TaskCommentsAddButton onAdd={onAddRow} />,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const isSaveDisabled = isSaving || currentCommentText.trim().length === 0;

        if (!isInEditMode) {
          return [];
        }

        return [
          <GridActionsCellItem
            key="save"
            icon={<ActionIconWithTooltip title="Save" iconName="save" />}
            label="Save comment"
            className="task-comments-modal-save-action"
            disabled={isSaveDisabled}
            onMouseDown={() => onMarkSaveIntent(id)}
            onClick={onSaveClick(id)}
          />,
          <GridActionsCellItem
            key="cancel"
            icon={<ActionIconWithTooltip title="Cancel" iconName="close" />}
            label="Cancel comment"
            className="task-comments-modal-cancel-action"
            onClick={onCancelClick(id)}
          />
        ];
      }
    }
  ];
}

function TaskCommentsAddButton({ onAdd }: { onAdd: () => void }) {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onAdd();
    },
    [onAdd]
  );

  return (
    <div className="task-comments-modal-add-header">
      <button type="button" className="task-comments-modal-add-button" onClick={handleClick}>
        <TaskIcon>add</TaskIcon> Add
      </button>
    </div>
  );
}

function ActionIconWithTooltip({ title, iconName }: { title: string; iconName: string }) {
  return (
    <Tooltip title={title} placement="top">
      <span>
        <TaskIcon>{iconName}</TaskIcon>
      </span>
    </Tooltip>
  );
}

function CommentEditCell({
  id,
  field,
  value,
  onCommentChange
}: GridRenderEditCellParams<TaskCommentRow, string> & {
  onCommentChange: (value: string) => void;
}) {
  const apiRef = useGridApiContext();
  const [commentValue, setCommentValue] = useState(value || '');

  useEffect(() => {
    setCommentValue(value || '');
  }, [value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      setCommentValue(newValue);
      onCommentChange(newValue);
      void apiRef.current.setEditCellValue({ id, field, value: newValue });
    },
    [apiRef, field, id, onCommentChange]
  );

  return (
    <div className="task-comments-modal-comment-edit-field">
      <textarea
        autoFocus
        aria-label="Comment"
        value={commentValue}
        maxLength={MAX_COMMENT_LENGTH}
        onChange={handleChange}
        onKeyDown={event => {
          event.stopPropagation();
        }}
        className="task-comments-modal-comment-edit-input"
      />
      <div className="task-comments-modal-comment-edit-count" aria-label="Comment character count">
        {commentValue.length}/{MAX_COMMENT_LENGTH}
      </div>
    </div>
  );
}
